import React from "react";
import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";

export type PublicMaterialKey = 'traditional' | 'peel' | 'peel_luxe';

/** One wall material, as served by GET /v2/public/pricing. All amounts are per sq ft. */
export interface IPublicMaterialPricing {
    key: PublicMaterialKey;
    label: string;
    artist_cost_sqft: number;
    store_owner_cost_sqft: number;
    royalty_basis_sqft: number;
}

/** What brands on Wallmates charge per sq ft today: 25th percentile, median and 90th percentile. */
export interface IPublicRetailRange {
    key: PublicMaterialKey;
    low: number;
    typical: number;
    high: number;
    sample_size: number;
}

/** The public artist pricing payload. Global rates only; nothing brand-specific. */
export interface IPublicPricing {
    currency: string;
    materials: IPublicMaterialPricing[];
    licensing_fee: { type: 'PERCENTAGE' | 'FLAT'; value: number };
    retail_ranges: IPublicRetailRange[];
    /** prepaid_top_up is absent from payloads cached before it was added. */
    store_owner_fees: { fulfillment_fee_percent: number; order_processing: number; prepaid_top_up?: number };
    panel_width_inches: number;
    royalty_exempt_panel_heights_ft: number[];
    payout: { schedule: 'monthly'; minimum: number; method: 'stripe_connect' };
    generated_at: string;
}

// The response is cached for minutes by browsers and CDNs. Bump `v` whenever the
// payload shape changes so a new build never reads an old-shape cached copy.
const pricingUrl = `${environment.v2Url}/public/pricing?v=2`;

// One request per page load, shared by every widget on the page.
let pending: Promise<IPublicPricing | null> | null = null;

// #region fetchPublicPricing
/** No login and no auth header, so it works on the dashboard and inside the wallmates.com embed. */
export const fetchPublicPricing = (): Promise<IPublicPricing | null> => {
    if (!pending) {
        pending = axios.get(pricingUrl)
            .then((r: AxiosResponse<IPublicPricing>) => ({ ...r.data, retail_ranges: r.data?.retail_ranges ?? [] }))
            .catch(() => { pending = null; return null; });
    }
    return pending;
};

// #region usePublicPricing
/** `undefined` while loading, `null` if the rates could not be loaded. */
export const usePublicPricing = (): IPublicPricing | null | undefined => {
    const [pricing, setPricing] = React.useState<IPublicPricing | null | undefined>(undefined);
    React.useEffect(() => {
        let alive = true;
        fetchPublicPricing().then((p) => { if (alive) setPricing(p); });
        return () => { alive = false; };
    }, []);
    return pricing;
};
